import type { JSX } from "react";
import type {
  CalculationType,
  FieldConfiguration,
} from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { DimensionField } from "../../../../../../../../../api/types/svTypes.ts";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel.tsx";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton.tsx";
import FieldOptionsDivider from "../../../../viewer/common/fields/FieldOptionsDivider.tsx";
import DimensionAggregation from "../../../common/fields/dimension-aggregation/DimensionAggregation.tsx";
import useDimensionFieldOptions from "../../../common/fields/dimensions/hooks/useDimensionFieldOptions.ts";
import CalculationFieldMenuItem from "../../../common/fields/field-options/CalculationFieldMenuItem.tsx";
import SuppressBlankValues from "../../../common/fields/field-options/SuppressBlankValues.tsx";
import DateGroupingComponent from "../../../tabular/table/DateGroupingComponent.tsx";
import { isNumericField } from "../../../utils/isNumericField.ts";

interface Props {
  field: DimensionField;
  AggregationOptions: JSX.Element;
  canAddCustomField: boolean | undefined;
  cancel: () => void;
  onRemove: () => void;
  onUpdateConfiguration: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onUpdateCustomLabel: (name: string | undefined) => void;
  onCalculationItemClick: (calculationType: CalculationType) => void | undefined;
  readonly: boolean;
}

export default function DimensionFieldOptions({
  field,
  AggregationOptions,
  canAddCustomField,
  cancel,
  onRemove,
  onUpdateConfiguration,
  onUpdateCustomLabel,
  onCalculationItemClick,
  readonly,
}: Props) {
  const { isNumeric, aggregation, showGroupings, handleRemove, updateGrouping, saveChanges, onUpdateAggregation } =
    useDimensionFieldOptions({
      field,
      cancel,
      onRemove,
      onUpdateConfiguration,
    });

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={onUpdateCustomLabel}
        readonly={readonly}
      />
      <FieldOptionsDivider />
      {isNumericField(field) && canAddCustomField && !readonly && (
        <CalculationFieldMenuItem onCalculationItemClick={onCalculationItemClick} />
      )}
      <FieldOptionsDivider />
      <DimensionAggregation
        field={field}
        onChange={onUpdateAggregation}
        hideAggregationType={!!field.config.persistedAggregationName}
        readonly={readonly}
      />
      {showGroupings && <DateGroupingComponent field={field} updateGrouping={updateGrouping} readonly={readonly} />}
      {aggregation && AggregationOptions}
      {aggregation && (
        <SuppressBlankValues
          field={field}
          onChanged={saveChanges}
          showExcludeZeroValues={field.config.hideBlankValues === true && isNumeric}
          readonly={readonly}
        />
      )}
      <FieldOptionsDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
}
